import "bootstrap-icons/font/bootstrap-icons.css";
import { Link } from "react-router-dom";
import { Badge, Button, Dropdown, Menu, Table, Tag, Select, Space, Popover, Tooltip } from "antd";
import { FilterOutlined, EyeOutlined, EditOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import moment from "moment";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadAllDiscountMaster } from "../../redux/actions/discountMaster/getDiscountMasterAction";
import { deleteDiscountMaster } from "../../redux/actions/discountMaster/deleteDiscountMasterAction";
import "./discountMaster.css";

const { Option } = Select;

const FILTER_STYLE = { marginBottom: 12 };
const FILTER_LABEL = { display: "block", marginBottom: 4, color: "rgba(0,0,0,0.65)", fontSize: 12 };

function CustomTable({ list, addDiscountMasterButton, uploadButton }) {
  const dispatch = useDispatch();
  const [columnItems, setColumnItems] = useState([]);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [discountTypeFilter, setDiscountTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [tableScrollY, setTableScrollY] = useState(400);
  const tableWrapRef = useRef(null);

  useEffect(() => {
    if (!tableWrapRef.current) return;
    const el = tableWrapRef.current;
    const ro = new ResizeObserver(() => {
      setTableScrollY(Math.max(200, el.clientHeight - 88));
    });
    ro.observe(el);
    setTableScrollY(Math.max(200, el.clientHeight - 88));
    return () => ro.disconnect();
  }, [list]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this discount?")) {
      await dispatch(deleteDiscountMaster(id));
      dispatch(loadAllDiscountMaster({ page: 1, limit: 10, discount_type: discountTypeFilter || undefined, status: statusFilter }));
    }
  };

  const columns = [
    {
      title: "S.No",
      key: "sNo",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Type",
      dataIndex: "discount_type",
      key: "discount_type",
      render: (type) => {
        const colors = {
          sale: "green",
          purchase: "orange",
        };
        return <Tag color={colors[type]}>{type?.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Publisher",
      dataIndex: ["publisher", "name"],
      key: "publisher_name",
    },
    {
      title: "Customer",
      dataIndex: ["customer", "name"],
      key: "customer_name",
      render: (name) => name || "-",
    },
    {
      title: "Supplier",
      dataIndex: ["supplier", "name"],
      key: "supplier_name",
      width: 100,
      ellipsis: true,
      render: (name) => name || "-",
    },
    {
      title: "Discount",
      dataIndex: "discount_value",
      key: "discount_value",
      render: (value, record) => {
        return record.discount_unit === "percentage" 
          ? `${value}%` 
          : `₹${value}`;
      },
    },
    {
      title: "Unit",
      dataIndex: "discount_unit",
      key: "discount_unit",
      render: (unit) => <Tag>{unit}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status ? "green" : "red"}>{status ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Effective From",
      dataIndex: "effective_from",
      key: "effective_from",
      render: (date) => date ? moment(date).format("YYYY-MM-DD") : "-",
    },
    {
      title: "Effective To",
      dataIndex: "effective_to",
      key: "effective_to",
      width: 115,
      ellipsis: true,
      render: (date) => date ? moment(date).format("YYYY-MM-DD") : "-",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Link to={`/discount-master/${record.id}`}>
              <Button type="link" size="small" icon={<EyeOutlined />} style={{ color: "#1890ff" }} />
            </Link>
          </Tooltip>
          <Tooltip title="Edit">
            <Link to={`/discount-master/${record.id}/update`}>
              <Button type="link" size="small" icon={<EditOutlined />} style={{ color: "#fa8c16" }} />
            </Link>
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
              style={{ color: "#ff4d4f" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const menuItems = columns.map((item) => ({
    key: item.key,
    label: <span>{item.title}</span>,
  }));

  useEffect(() => {
    setColumnItems(menuItems);
    setColumnsToShow(columns);
  }, []);

  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      const filteredColumnsToShow = columnsToShow.filter((item) => item.key !== col.key);
      setColumnsToShow(filteredColumnsToShow);
    } else {
      const foundIndex = columns.findIndex((item) => item.key === col.key);
      const foundCol = columns.find((item) => item.key === col.key);
      let updatedColumnsToShow = [...columnsToShow];
      updatedColumnsToShow.splice(foundIndex, 0, foundCol);
      setColumnsToShow(updatedColumnsToShow);
    }
  };

  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));

  const activeFilterCount = [discountTypeFilter, statusFilter].filter(
    (v) => v !== undefined && v !== ""
  ).length;

  const filterContent = (
    <div style={{ width: 260 }}>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Discount Type</label>
        <Select
          placeholder="All Types"
          allowClear
          style={{ width: "100%" }}
          value={discountTypeFilter || undefined}
          onChange={(value) => {
            setDiscountTypeFilter(value ?? "");
            dispatch(loadAllDiscountMaster({ page: 1, limit: 10, discount_type: value ?? undefined, status: statusFilter }));
          }}
        >
          <Option value="sale">Sale</Option>
          <Option value="purchase">Purchase</Option>
        </Select>
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Status</label>
        <Select
          placeholder="All Status"
          allowClear
          style={{ width: "100%" }}
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            dispatch(loadAllDiscountMaster({ page: 1, limit: 10, discount_type: discountTypeFilter || undefined, status: value }));
          }}
        >
          <Option value={true}>Active</Option>
          <Option value={false}>Inactive</Option>
        </Select>
      </div>
      <div style={{ textAlign: "center", marginTop: 8 }}>
        <Button
          size="small"
          onClick={() => {
            setDiscountTypeFilter("");
            setStatusFilter(undefined);
            dispatch(loadAllDiscountMaster({ page: 1, limit: 10 }));
          }}
        >
          Clear All Filters
        </Button>
      </div>
    </div>
  );

  return (
    <div className="card column-design list-page-fill-viewport">
      <div className="card-body">
        <div className="card-title d-flex justify-content-between align-items-center list-page-header" style={{ marginBottom: 0 }}>
          <h5 className="mb-0">Discount Master List</h5>
          <div className="discount-master-header-actions">
            {list && (
              <>
                <Popover
                  content={filterContent}
                  title="Filters"
                  trigger="click"
                  open={filterVisible}
                  onOpenChange={setFilterVisible}
                  placement="bottomLeft"
                >
                  <Badge count={activeFilterCount} size="small" offset={[-2, 2]}>
                    <Button
                      type="primary"
                      className="filter-icon-btn"
                      icon={<FilterOutlined />}
                      onClick={() => setFilterVisible((v) => !v)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  </Badge>
                </Popover>
                <Dropdown
                  overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
                  placement="bottomRight"
                >
                  <Button className="column-visibility">Column Visibility</Button>
                </Dropdown>
              </>
            )}
            {uploadButton}
            {addDiscountMasterButton}
          </div>
        </div>

        <div className="list-page-table-container">
          <div className="list-page-table-inner">
            <div ref={tableWrapRef} className="list-page-table-wrap">
              {/* Desktop Table */}
              <div className="d-none d-lg-block">
                <Table
                  scroll={{ x: true, y: tableScrollY }}
                  loading={!list}
                  pagination={{
                    defaultPageSize: 10,
                    pageSizeOptions: [10, 20, 50, 100, 200],
                    showSizeChanger: true,
                    onChange: (page, limit) => {
                      dispatch(loadAllDiscountMaster({ page, limit, discount_type: discountTypeFilter || undefined, status: statusFilter }));
                    },
                  }}
                  columns={columnsToShow}
                  dataSource={list ? addKeys(list) : []}
                />
              </div>

              {/* Mobile: Discount cards (instead of table) */}
              <div className="d-lg-none discount-master-mobile-cards">
                {list && list.length > 0 ? (
                  <div className="row">
                    {list.map((record, idx) => (
                      <div key={record.id ?? idx} className="col-12 mb-3">
                        <div className="discount-master-mobile-card card">
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between gap-2">
                              <div className="discount-master-mobile-title">
                                <div className="mb-1">
                                  {record.discount_type ? (
                                    (() => {
                                      const colors = { sale: "green", purchase: "orange" };
                                      const color = colors[record.discount_type] || "blue";
                                      return <Tag color={color}>{String(record.discount_type).toUpperCase()}</Tag>;
                                    })()
                                  ) : null}
                                </div>
                                <div className="discount-master-mobile-name">
                                  <strong>{record.publisher?.name || "-"}</strong>
                                </div>
                              </div>

                              <div className="discount-master-mobile-actions">
                                <Dropdown
                                  trigger={["click"]}
                                  placement="bottomRight"
                                  overlay={
                                    <Menu>
                                      <Menu.Item key="view" icon={<EyeOutlined />}>
                                        <Link to={`/discount-master/${record.id}`}>View</Link>
                                      </Menu.Item>
                                      <Menu.Item key="edit" icon={<EditOutlined />}>
                                        <Link to={`/discount-master/${record.id}/update`}>Edit</Link>
                                      </Menu.Item>
                                      <Menu.Item
                                        key="delete"
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleDelete(record.id)}
                                        style={{ color: "#ff4d4f" }}
                                      >
                                        Delete
                                      </Menu.Item>
                                    </Menu>
                                  }
                                >
                                  <Button
                                    type="text"
                                    size="small"
                                    className="discount-master-mobile-kebab"
                                    icon={<MoreOutlined />}
                                  />
                                </Dropdown>
                              </div>
                            </div>

                            <div className="discount-master-mobile-grid">
                              <div className="discount-master-mobile-row">
                                <span className="discount-master-mobile-label">Customer</span>
                                <span className="discount-master-mobile-value">{record.customer?.name || "-"}</span>
                              </div>
                              <div className="discount-master-mobile-row">
                                <span className="discount-master-mobile-label">Supplier</span>
                                <span className="discount-master-mobile-value">{record.supplier?.name || "-"}</span>
                              </div>
                              <div className="discount-master-mobile-row">
                                <span className="discount-master-mobile-label">Discount</span>
                                <span className="discount-master-mobile-value">
                                  {record.discount_unit === "percentage"
                                    ? `${record.discount_value ?? 0}%`
                                    : `₹${record.discount_value ?? 0}`}
                                </span>
                              </div>
                              <div className="discount-master-mobile-row">
                                <span className="discount-master-mobile-label">Status</span>
                                <span className="discount-master-mobile-value">
                                  {record.status ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>}
                                </span>
                              </div>
                              <div className="discount-master-mobile-row">
                                <span className="discount-master-mobile-label">Effective From</span>
                                <span className="discount-master-mobile-value">
                                  {record.effective_from ? moment(record.effective_from).format("YYYY-MM-DD") : "-"}
                                </span>
                              </div>
                              <div className="discount-master-mobile-row">
                                <span className="discount-master-mobile-label">Effective To</span>
                                <span className="discount-master-mobile-value">
                                  {record.effective_to ? moment(record.effective_to).format("YYYY-MM-DD") : "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted mb-0">No Discount Masters found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const GetAllDiscountMaster = ({ refreshTrigger, addDiscountMasterButton, uploadButton }) => {
  const dispatch = useDispatch();
  const list = useSelector((state) => state.discountMasters?.list);

  useEffect(() => {
    dispatch(loadAllDiscountMaster({ page: 1, limit: 10 }));
  }, [dispatch, refreshTrigger]);

  return (
    <CustomTable
      list={list}
      addDiscountMasterButton={addDiscountMasterButton}
      uploadButton={uploadButton}
    />
  );
};

export default GetAllDiscountMaster;
